import { getAudioContext } from './sharedAudio';

/**
 * The microphone as frames of float samples, for the live ears. The stream asks for echo cancellation and noise
 * suppression (so a reply played from the phone is not heard as the person); an AudioWorklet reads it, with a
 * ScriptProcessor behind it for browsers without worklets. Created inside the tap that asked for it — iOS only lets an
 * AudioContext run, and only asks for the microphone, from a gesture.
 */
const WORKLET = `
class Tap extends AudioWorkletProcessor {
  constructor() { super(); this.buf = []; this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      this.buf.push(new Float32Array(ch)); this.n += ch.length;
      if (this.n >= sampleRate * 0.02) {
        const out = new Float32Array(this.n); let o = 0;
        for (const b of this.buf) { out.set(b, o); o += b.length; }
        this.buf = []; this.n = 0;
        this.port.postMessage(out, [out.buffer]);
      }
    }
    return true;
  }
}
registerProcessor('stylon-tap', Tap);
`;

export function canCapture() {
  return typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && !!(window.AudioContext || window.webkitAudioContext) && typeof window.WebSocket === 'function';
}

/** Opens the microphone. Resolves to { sampleRate, stop }; onFrame(float32, sampleRate) is called about every 20 ms. */
export async function createCapture({ onFrame, stream: given } = {}) {
  // The ONE shared context (sharedAudio.js), made in the gesture; it is resumed after the microphone is open.
  const ctx = getAudioContext();
  let stream = given;
  try {
    stream = stream || (await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } }));
  } catch (e) {
    throw e; // the shared context stays: the next press, or the mouth, uses it
  }
  try { await ctx.resume(); } catch (_) { /* a context that is already running */ }
  const source = ctx.createMediaStreamSource(stream);
  const silent = ctx.createGain();
  silent.gain.value = 0; // the graph must reach the destination to run; nothing is played back
  let node;
  if (ctx.audioWorklet && typeof Blob !== 'undefined' && typeof URL?.createObjectURL === 'function') {
    try {
      const url = URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' }));
      await ctx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      node = new AudioWorkletNode(ctx, 'stylon-tap');
      node.port.onmessage = (e) => onFrame?.(e.data, ctx.sampleRate);
    } catch (_) { node = null; }
  }
  if (!node) {
    node = ctx.createScriptProcessor(1024, 1, 1);
    node.onaudioprocess = (e) => onFrame?.(new Float32Array(e.inputBuffer.getChannelData(0)), ctx.sampleRate);
  }
  source.connect(node);
  node.connect(silent);
  silent.connect(ctx.destination);
  return {
    sampleRate: ctx.sampleRate,
    stop() {
      try { node.disconnect(); source.disconnect(); } catch (_) { /* already */ }
      if (!given) stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) { /* gone */ } });
      // The shared context is not closed here: the mouth speaks through it, and the next press listens through it.
    },
  };
}
